package com.fantasytenis.shared.data.lineup.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.lineup.api.LineupApi
import com.fantasytenis.shared.data.lineup.dto.*
import com.fantasytenis.shared.domain.lineup.entity.*

/**
 * Validation result from lineup validation.
 */
data class LineupValidationResult(
    val isValid: Boolean,
    val errors: List<String>,
    val warnings: List<String>,
    val totalCost: Int,
    val remainingBudget: Int,
    val slotCosts: SlotCosts
)

/**
 * Can add player result.
 */
data class CanAddPlayerResult(
    val canAdd: Boolean,
    val newTotal: Int,
    val reason: String?
)

/**
 * Lineup with validation result.
 */
data class LineupWithValidation(
    val lineup: LineupEntity,
    val validation: LineupValidationResult?
)

/**
 * Lineup Repository Interface.
 *
 * Defines the contract for lineup/roster data operations.
 */
interface LineupRepository {
    suspend fun createLineup(
        contestEntryId: String,
        contestId: String,
        slots: LineupSlots?
    ): Result<LineupWithValidation>

    suspend fun updateLineup(
        lineupId: String,
        slots: LineupSlots
    ): Result<LineupWithValidation>

    suspend fun getLineup(lineupId: String): Result<LineupEntity>

    suspend fun getUserLineups(contestId: String? = null): Result<List<LineupEntity>>

    suspend fun validateLineup(
        contestId: String,
        slots: LineupSlots
    ): Result<LineupValidationResult>

    suspend fun canAddPlayer(
        lineupId: String,
        category: Category,
        playerId: String
    ): Result<CanAddPlayerResult>

    suspend fun lockLineup(lineupId: String): Result<LineupEntity>
}

/**
 * Lineup Repository Implementation.
 */
class LineupRepositoryImpl(
    private val lineupApi: LineupApi
) : LineupRepository {

    override suspend fun createLineup(
        contestEntryId: String,
        contestId: String,
        slots: LineupSlots?
    ): Result<LineupWithValidation> {
        val request = CreateLineupRequest(
            contestEntryId = contestEntryId,
            contestId = contestId,
            slots = slots?.toDto()
        )

        return when (val response = lineupApi.createLineup(request)) {
            is ApiResponse.Success -> {
                val data = response.data
                if (data.data != null) {
                    Result.success(
                        LineupWithValidation(
                            lineup = data.data.toEntity(),
                            validation = data.validation?.toResult()
                        )
                    )
                } else {
                    Result.failure(Exception("No lineup data returned"))
                }
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun updateLineup(
        lineupId: String,
        slots: LineupSlots
    ): Result<LineupWithValidation> {
        val request = UpdateLineupRequest(slots = slots.toDto())

        return when (val response = lineupApi.updateLineup(lineupId, request)) {
            is ApiResponse.Success -> {
                val data = response.data
                if (data.data != null) {
                    Result.success(
                        LineupWithValidation(
                            lineup = data.data.toEntity(),
                            validation = data.validation?.toResult()
                        )
                    )
                } else {
                    Result.failure(Exception("No lineup data returned"))
                }
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getLineup(lineupId: String): Result<LineupEntity> {
        return when (val response = lineupApi.getLineup(lineupId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getUserLineups(contestId: String?): Result<List<LineupEntity>> {
        return when (val response = lineupApi.getUserLineups(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun validateLineup(
        contestId: String,
        slots: LineupSlots
    ): Result<LineupValidationResult> {
        val request = ValidateLineupRequest(
            contestId = contestId,
            slots = slots.toDto()
        )

        return when (val response = lineupApi.validateLineup(request)) {
            is ApiResponse.Success -> Result.success(response.data.toResult())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun canAddPlayer(
        lineupId: String,
        category: Category,
        playerId: String
    ): Result<CanAddPlayerResult> {
        val request = CanAddPlayerRequest(
            category = category.name.lowercase(),
            playerId = playerId
        )

        return when (val response = lineupApi.canAddPlayer(lineupId, request)) {
            is ApiResponse.Success -> {
                Result.success(
                    CanAddPlayerResult(
                        canAdd = response.data.canAdd,
                        newTotal = response.data.newTotal,
                        reason = response.data.reason
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun lockLineup(lineupId: String): Result<LineupEntity> {
        return when (val response = lineupApi.lockLineup(lineupId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

// ===== DTO TO ENTITY MAPPERS =====

private fun LineupSlots.toDto(): LineupSlotsDto {
    return LineupSlotsDto(
        serve = serve,
        forehand = forehand,
        backhand = backhand,
        volleys = volleys,
        fighting = fighting
    )
}

private fun LineupDto.toEntity(): LineupEntity {
    return LineupEntity(
        id = id,
        contestEntryId = contestEntryId,
        userId = userId,
        contestId = contestId,
        slots = LineupSlots(
            serve = slots.serve,
            forehand = slots.forehand,
            backhand = slots.backhand,
            volleys = slots.volleys,
            fighting = slots.fighting
        ),
        totalSalaryCost = totalSalaryCost,
        remainingBudget = remainingBudget,
        salaryCap = salaryCap,
        isValid = isValid,
        isLocked = isLocked,
        validationErrors = validationErrors,
        totalPoints = totalPoints,
        categoryScores = categoryScores?.let {
            CategoryScores(
                serve = it.serve,
                forehand = it.forehand,
                backhand = it.backhand,
                volleys = it.volleys,
                fighting = it.fighting
            )
        },
        categoryBreakdowns = categoryBreakdowns?.toEntity(),
        filledSlots = filledSlots
    )
}

private fun CategoryBreakdownsDto.toEntity(): CategoryBreakdowns {
    return CategoryBreakdowns(
        serve = serve?.toEntity(),
        forehand = forehand?.toEntity(),
        backhand = backhand?.toEntity(),
        volleys = volleys?.toEntity(),
        fighting = fighting?.toEntity()
    )
}

private fun CategoryBreakdownDto.toEntity(): CategoryBreakdown {
    return CategoryBreakdown(
        stats = stats.map { it.toEntity() },
        quickFinishBonus = quickFinishBonus,
        total = total
    )
}

private fun StatLineItemDto.toEntity(): StatLineItem {
    return StatLineItem(
        stat = stat,
        count = count,
        multiplier = multiplier,
        points = points
    )
}

private fun ValidationResultDto.toResult(): LineupValidationResult {
    return LineupValidationResult(
        isValid = isValid,
        errors = errors,
        warnings = warnings,
        totalCost = totalCost,
        remainingBudget = remainingBudget,
        slotCosts = SlotCosts(
            serve = slotCosts.serve,
            forehand = slotCosts.forehand,
            backhand = slotCosts.backhand,
            volleys = slotCosts.volleys,
            fighting = slotCosts.fighting
        )
    )
}
